import type { CompiledEntityTemplate, FrameworkTemplateField } from "@repo/shared";
import type { FieldConfig } from "../nodes/types";
import type { DocumentEntityType, DocumentSchemaConfig, DocumentTabMap } from "./types";

export function createDocumentSchemaConfig(input: {
  entityType: DocumentEntityType;
  displayName: string;
  tabKey: string;
  contentExpression: string;
  contentMode?: "structured" | "freeform";
  fields: readonly FrameworkTemplateField[];
}): DocumentSchemaConfig {
  return {
    entityType: input.entityType,
    displayName: input.displayName,
    tabKey: input.tabKey,
    contentExpression: input.contentExpression,
    contentMode: input.contentMode,
    fields: input.fields.map((field) => toFieldConfig(field, input.contentMode)),
  };
}

export function createDocumentTabMapFromCompiledTemplate(input: {
  entityType: DocumentEntityType;
  displayName: string;
  template: CompiledEntityTemplate;
}): DocumentTabMap {
  return createDocumentTabMap({
    entityType: input.entityType,
    displayName: input.displayName,
    contentMode: input.template.contentMode,
    tabs: input.template.tabs,
  });
}

export function createDocumentTabMap(input: {
  entityType: DocumentEntityType;
  displayName: string;
  contentMode?: "structured" | "freeform";
  tabs: readonly Pick<
    CompiledEntityTemplate["tabs"][number],
    "tabKey" | "contentExpression" | "fields"
  >[];
}): DocumentTabMap {
  return Object.fromEntries(
    input.tabs.map((tab) => [
      tab.tabKey,
      createDocumentSchemaConfig({
        entityType: input.entityType,
        displayName: input.displayName,
        tabKey: tab.tabKey,
        contentExpression: tab.contentExpression,
        contentMode: input.contentMode,
        fields: tab.fields,
      }),
    ])
  );
}

function toFieldConfig(
  field: FrameworkTemplateField,
  contentMode?: "structured" | "freeform"
): FieldConfig {
  const required = field.required === true;
  const guidance =
    typeof field.guidance === "string" && field.guidance.trim().length > 0
      ? field.guidance.trim()
      : undefined;
  const expectedMentionTypes =
    Array.isArray(field.expectedMentionTypes) && field.expectedMentionTypes.length > 0
      ? field.expectedMentionTypes.filter((option): option is string => typeof option === "string")
      : undefined;

  return {
    nodeType: "richSection",
    fieldId: field.fieldId,
    label: field.label,
    required,
    guidance,
    expectedMentionTypes,
    ...(contentMode === "freeform" ? { chromeless: true } : {}),
  };
}
